import { AnalyticsStore, codeSplit, Controller, FetchOptions, FetchPageOptions, PageStore } from "bernie-core";
import { PageData } from "bernie-http";
import { BlossomCodeSplitter } from "src/components/flexFramework/CodeSplitter/BlossomCodeSplitter";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { CookiesStore } from "stores/CookiesStore";
import { DXStore } from "stores/DXStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { ServerWizardStateStore } from "stores/wizard/ServerWizardStateStore";
import { Composition, ExtendedContextStore, ViewModelResponse } from "typings/flexFramework/FlexDefinitions";
import { ServerChatbotStore } from "src/stores/chatbotStore/ServerChatbotStore";
import { MabStore } from "stores/mabStore/MabStore";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    compositionStore: CompositionStore;
  };
}

export class LandingController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  public path = "/*";
  public routeName = "flex-LandingPage";
  public bundles = ["flex-LandingPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-LandingPage" */ "views/LandingPage") as any;
    let composition: Composition | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = ((window as unknown) as WindowWithState).__STATE__;
      composition =
        serializedState && serializedState.compositionStore ? serializedState.compositionStore.composition : undefined;
    }

    await new BlossomCodeSplitter().loadRequiredCodeSplitBundles({ composition });

    return routeComponent;
  });

  public async fetch(options: FetchOptions): Promise<any> {
    const { stores, request } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const cookiesStore = stores && stores.get<CookiesStore>("cookies");
    const mapStore = stores && stores.get<StaticMapStore>("staticMap");
    const compositionStore = stores && stores.get<CompositionStore>("compositionStore");
    const flexModuleModelStore = stores && stores.get<FlexModuleModelStore>("flexModuleModelStore");
    const wizardState = stores && stores.get<ServerWizardStateStore>("wizardState");
    const dxStore = stores && stores.get<DXStore>("dx");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const chatbotStore = stores && stores.get<ServerChatbotStore>("chatbot");
    const mabStore = stores && stores.get<MabStore>("mab");

    //  Get cookie string for wizard
    cookiesStore.cookie = (request && request.headers && request.headers.cookie) || "";
    if (wizardState && wizardState.setLastSearchState) {
      wizardState.setLastSearchState(cookiesStore.getCookie("lastSearchCookie")!, contextStore);
    }

    // We get it from the middleware which hijacks the contextStore
    const viewModelResponse = contextStore.viewModelResponse as ViewModelResponse;

    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(viewModelResponse, pageStore, contextStore, analyticsStore, footer);
    mabStore.setVariantsAndLog(viewModelResponse?.flexContext, analyticsStore);

    compositionStore.setup(viewModelResponse, contextStore, flexModuleModelStore, mapStore);

    dxStore.setupInitialJumplinks(viewModelResponse);

    if (typeof chatbotStore.getTokenFromAuthorizer === "function") {
      await chatbotStore.getTokenFromAuthorizer(request, contextStore);
    }

    return Promise.resolve({});
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const viewModelResponse = options.context.viewModelResponse as ViewModelResponse;
    const additionalScripts = new BlossomCodeSplitter().getRequiredWebpackChunkNames({
      composition: viewModelResponse.composition,
    });

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(({ default: _ }) => {
      return (
        _.buildPageDataWithSeoMetaDataFromViewModel(viewModelResponse) || {
          title: viewModelResponse.composition.title,
        }
      );
    });

    // Basically giving a list of webpackChunkNames that we want
    return Promise.resolve(seoMetaData).then((m) => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey),
        pageId: options.context.pageId,
      };
    });
  }
}
